// Welfare assistance requests, the needy list and official approval ("vetting").
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../utils/axios.ts';
import getApiErrorMessage from '../utils/helper.js';

export interface AssistanceRequest {
  id: number;
  member_id: number;
  title: string;
  category: string;
  description?: string | null;
  amount_requested: string;
  unpaid_welfare_debts_count: number;
  is_flagged: boolean;
  flag_reason?: string | null;
  status: 'pending_vetting' | 'approved' | 'rejected' | 'completed';
  required_vettings_count: number;
  in_needy_list: boolean;
  welfare_plan_id?: number | null;
  approved_at?: string | null;
  created_at: string;
  member?: { id: number; name: string; phone_number?: string; user_id?: number | null };
  vettings?: { id: number; user_id: number; action: 'approve' | 'reject'; notes?: string | null; official?: { id: number; name: string } }[];
}

export const useAssistanceRequests = (status: string) =>
  useQuery<AssistanceRequest[]>({
    queryKey: ['welfare-assistance', 'requests', status],
    queryFn: async () => (await api.get('/welfare-assistance/requests', { params: { status, per_page: 100 } })).data.data,
  });

export const useNeedyList = () =>
  useQuery<AssistanceRequest[]>({
    queryKey: ['welfare-assistance', 'needy'],
    queryFn: async () => (await api.get('/welfare-assistance/needy-list', { params: { per_page: 100 } })).data.data,
  });

const useInvalidate = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['welfare-assistance'] });
};

export const useCreateAssistanceRequest = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: { member_id: number; title: string; category?: string; description?: string; amount_requested: number }) =>
      (await api.post('/welfare-assistance/requests', payload)).data,
    onSuccess: (data) => { toast.success(data?.message || 'Request saved'); invalidate(); },
    onError: (error: any) => toast.error(getApiErrorMessage(error) || 'Could not save the request'),
  });
};

export const useVetRequest = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, action, notes }: { id: number; action: 'approve' | 'reject'; notes?: string }) =>
      (await api.post(`/welfare-assistance/requests/${id}/vet`, { action, notes })).data,
    onSuccess: (data) => { toast.success(data?.message || 'Saved'); invalidate(); },
    onError: (error: any) => toast.error(getApiErrorMessage(error) || 'Could not record your decision'),
  });
};

export const useCreateWelfarePlanFromRequest = () => {
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, mpesa_credential_id }: { id: number; mpesa_credential_id?: number | '' }) =>
      (await api.post(`/welfare-assistance/requests/${id}/create-welfare-plan`, { mpesa_credential_id: mpesa_credential_id || null })).data,
    onSuccess: (data) => {
      toast.success(data?.message || 'Welfare plan created');
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['welfare'] });
    },
    onError: (error: any) => toast.error(getApiErrorMessage(error) || 'Could not create the welfare plan'),
  });
};

export interface MemberHistory {
  member: { id: number; name: string };
  eligibility: { qualified: boolean; unpaid_debts_count: number; is_flagged: boolean; flag_reason?: string | null };
  grace_days: number;
  debt_limit: number;
  owing: { plan_id: number; plan_name: string; for?: string | null; plan_status: string; since?: string | null }[];
  paid: { plans: number; total: number };
  received: { plan_id: number; plan_name: string; raised: number }[];
  requests: { id: number; title: string; amount: number; status: string; date?: string | null }[];
}

/** A member's welfare record: what they owe, what they paid and what they asked for before. Loads only when wanted. */
export const useMemberHistory = (memberId: number | null | undefined, enabled = true, exceptRequestId?: number) =>
  useQuery<MemberHistory>({
    queryKey: ['welfare-assistance', 'history', memberId, exceptRequestId],
    queryFn: async () => (await api.get(`/welfare-assistance/members/${memberId}/history`, { params: { except: exceptRequestId } })).data.data,
    enabled: !!memberId && enabled,
  });
